/**
 * Browser Notification Service
 * Handles browser notification permissions and settings
 */

class NotificationService {
  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from the browser
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Browser does not support notifications');
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    // Request permission
    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Check if notifications are enabled (both permission granted and user preference)
   */
  isEnabled(): boolean {
    return this.getPermission() === 'granted';
  }

  /**
   * Show a test notification (for testing purposes)
   */
  async showTestNotification(): Promise<void> {
    if (!this.isEnabled()) {
      throw new Error('Notifications are not enabled');
    }

    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification('ZivoHealth', {
      body: 'Notifications are working!',
      icon: '/images/zivo_logo.png',
      badge: '/images/zivo_logo.png',
      tag: 'test-notification',
    });
  }
}

export const notificationService = new NotificationService();
